import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, X, Film, CheckCircle2, AlertCircle, Info, ChevronDown, ChevronUp } from 'lucide-react';
import { createFullListing, FullListingPayload } from '../services/api';
import { uploadToCloudinary, uploadVideoToCloudinary } from '../services/cloudinary';

// ── Types ────────────────────────────────────────────────────────────────────
interface PhotoSlot {
  label: string;
  emoji: string;
  required: boolean;
  url: string | null;
  uploading: boolean;
  progress: number;
}

// ── Constants ────────────────────────────────────────────────────────────────
const CATEGORIES = [
  { value: 'fiction', label: 'Fiction' },
  { value: 'non_fiction', label: 'Non-Fiction' },
  { value: 'educational', label: 'Educational' },
  { value: 'sinhala', label: 'Sinhala' },
  { value: 'children', label: 'Children' },
  { value: 'comics', label: 'Comics' },
];

const CONDITIONS = [
  { value: 'like_new', label: 'Like New' },
  { value: 'good', label: 'Good' },
  { value: 'fair', label: 'Fair' },
  { value: 'poor', label: 'Poor' },
];

const DISCOUNT_OPTIONS = [5, 10, 15, 20, 25, 30, 40, 50, 60, 70];

const ACADEMIC_YEARS = [
  '1st Year', '2nd Year', '3rd Year', '4th Year', '5th Year',
  'Postgrad', 'AL', 'OL',
];

// ── Component ────────────────────────────────────────────────────────────────
const CreateListing: React.FC = () => {
  const navigate = useNavigate();

  // ── Section 1: Basic Info ──────────────────────────────────────────────────
  const [title, setTitle] = useState('');
  const [author, setAuthor] = useState('');
  const [category, setCategory] = useState('fiction');
  const [condition, setCondition] = useState('like_new');
  const [priceLkr, setPriceLkr] = useState<number>(0);
  const [description, setDescription] = useState('');

  // ── Section 2: Photos ──────────────────────────────────────────────────────
  const [photoSlots, setPhotoSlots] = useState<PhotoSlot[]>([
    { label: 'Front Cover', emoji: '📷', required: true, url: null, uploading: false, progress: 0 },
    { label: 'Back Cover', emoji: '📷', required: true, url: null, uploading: false, progress: 0 },
    { label: 'Condition / Spine', emoji: '📷', required: true, url: null, uploading: false, progress: 0 },
  ]);
  const [extraPhotos, setExtraPhotos] = useState<string[]>([]);
  const [extraPhotoUploading, setExtraPhotoUploading] = useState(false);

  // ── Section 3: Pricing & Discount ──────────────────────────────────────────
  const [applyDiscount, setApplyDiscount] = useState(false);
  const [originalPrice, setOriginalPrice] = useState<number>(0);
  const [discountPct, setDiscountPct] = useState<number>(10);

  // ── Section 4: Paper Quality ───────────────────────────────────────────────
  const [paperType, setPaperType] = useState<string | null>(null);

  // ── Section 5: Book Bundle ─────────────────────────────────────────────────
  const [isBundle, setIsBundle] = useState(false);
  const [bundleBookCount, setBundleBookCount] = useState<number>(2);

  // ── Section 6: Kuppi Kade ──────────────────────────────────────────────────
  const [isAcademic, setIsAcademic] = useState(false);
  const [institution, setInstitution] = useState('');
  const [degreeStream, setDegreeStream] = useState('');
  const [academicYear, setAcademicYear] = useState('1st Year');

  // ── Section 7: Weekend Maradana ────────────────────────────────────────────
  const [isWeekendDrop, setIsWeekendDrop] = useState(false);
  const [weekendDropPrice, setWeekendDropPrice] = useState<number>(0);

  // ── Section 8: Flip-Test Video ─────────────────────────────────────────────
  const [flipTestVideoUrl, setFlipTestVideoUrl] = useState<string | null>(null);
  const [videoUploading, setVideoUploading] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [videoFileName, setVideoFileName] = useState('');

  // ── Section 9: Kilo Pettiya ────────────────────────────────────────────────
  const [isMysteryBox, setIsMysteryBox] = useState(false);
  const [boxWeightKg, setBoxWeightKg] = useState<number>(1);
  const [boxGenreDescription, setBoxGenreDescription] = useState('');

  // ── Section 10: Pin Potha ──────────────────────────────────────────────────
  const [pinPothaEligible, setPinPothaEligible] = useState(false);

  // ── Form State ─────────────────────────────────────────────────────────────
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // ── Collapsible sections ───────────────────────────────────────────────────
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    basic: true,
    photos: true,
    pricing: false,
    paper: false,
    bundle: false,
    kuppiKade: false,
    maradana: false,
    flipTest: false,
    kiloPettiya: false,
    pinPotha: false,
  });

  const toggleSection = (key: string) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // ── Toast ──────────────────────────────────────────────────────────────────
  const showToast = useCallback((msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3500);
  }, []);

  // ── Photo Upload Handler ───────────────────────────────────────────────────
  const handlePhotoSlotUpload = async (index: number, file: File) => {
    setPhotoSlots(prev => prev.map((s, i) => i === index ? { ...s, uploading: true, progress: 0 } : s));
    try {
      const url = await uploadToCloudinary(file, (progress) => {
        setPhotoSlots(prev => prev.map((s, i) => i === index ? { ...s, progress } : s));
      }, 'readora/listings');
      setPhotoSlots(prev => prev.map((s, i) => i === index ? { ...s, url, uploading: false, progress: 100 } : s));
    } catch {
      showToast('Photo upload failed');
      setPhotoSlots(prev => prev.map((s, i) => i === index ? { ...s, uploading: false, progress: 0 } : s));
    }
  };

  const handleExtraPhotoUpload = async (file: File) => {
    if (extraPhotos.length >= 5) {
      showToast('Maximum 8 photos total');
      return;
    }
    setExtraPhotoUploading(true);
    try {
      const url = await uploadToCloudinary(file, undefined, 'readora/listings');
      setExtraPhotos(prev => [...prev, url]);
    } catch {
      showToast('Photo upload failed');
    } finally {
      setExtraPhotoUploading(false);
    }
  };

  const removePhotoSlot = (index: number) => {
    setPhotoSlots(prev => prev.map((s, i) => i === index ? { ...s, url: null, progress: 0 } : s));
  };

  const removeExtraPhoto = (index: number) => {
    setExtraPhotos(prev => prev.filter((_, i) => i !== index));
  };

  // ── Video Upload Handler ───────────────────────────────────────────────────
  const handleVideoUpload = async (file: File) => {
    // Validate type
    if (file.type !== 'video/mp4') {
      showToast('Only MP4 files are accepted');
      return;
    }
    // Validate size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      showToast('Video must be under 10MB');
      return;
    }

    setVideoUploading(true);
    setVideoProgress(0);
    setVideoFileName(file.name);
    try {
      const url = await uploadVideoToCloudinary(file, (progress) => {
        setVideoProgress(progress);
      });
      setFlipTestVideoUrl(url);
      showToast('Video uploaded successfully ✅');
    } catch {
      showToast('Video upload failed');
    } finally {
      setVideoUploading(false);
    }
  };

  // ── Computed Values ────────────────────────────────────────────────────────
  const allPhotos = [
    ...photoSlots.filter(s => s.url).map(s => s.url!),
    ...extraPhotos,
  ];

  const discountedPrice = applyDiscount && originalPrice > 0
    ? Math.round(originalPrice * (1 - discountPct / 100))
    : priceLkr;

  const effectivePrice = applyDiscount ? discountedPrice : priceLkr;

  const needsFlipTest = effectivePrice >= 2000;

  // ── Validation ─────────────────────────────────────────────────────────────
  const validate = (): string[] => {
    const errs: string[] = [];

    if (!isMysteryBox) {
      if (!title.trim()) errs.push('Book title is required');
      if (!author.trim()) errs.push('Author is required');
    }
    if (priceLkr <= 0) errs.push('Price must be greater than 0');
    if (!condition) errs.push('Condition is required');

    // Photos validation
    const mandatoryPhotos = photoSlots.filter(s => s.required && s.url).length;
    if (mandatoryPhotos < 3) {
      errs.push('Minimum 3 photos required (Front · Back · Condition)');
    }

    // Flip-test validation
    if (needsFlipTest && !flipTestVideoUrl) {
      errs.push('Flip-Test video required for listings LKR 2,000 and above');
    }

    // Discount validation
    if (applyDiscount && originalPrice <= 0) {
      errs.push('Original price is required when discount is applied');
    }

    // Bundle validation
    if (isBundle && (bundleBookCount < 2 || bundleBookCount > 10)) {
      errs.push('Bundle must contain 2-10 books');
    }

    // Academic validation
    if (isAcademic && !institution.trim()) {
      errs.push('Institution is required for Kuppi Kade listings');
    }

    // Weekend drop validation
    if (isWeekendDrop && weekendDropPrice <= 0) {
      errs.push('Weekend drop price is required');
    }

    // Mystery box validation
    if (isMysteryBox) {
      if (boxWeightKg <= 0) errs.push('Box weight is required for Kilo Pettiya');
      if (!boxGenreDescription.trim()) errs.push('Genre description is required for Kilo Pettiya');
    }

    return errs;
  };

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    const validationErrors = validate();
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setErrors([]);
    setSaving(true);

    const payload: FullListingPayload = {
      title: isMysteryBox ? `Kilo Pettiya - ${boxGenreDescription.slice(0, 50)}` : title,
      author: isMysteryBox ? 'Various' : author,
      category: isMysteryBox ? 'bundle' : category,
      condition,
      price_lkr: applyDiscount ? discountedPrice : priceLkr,
      description,
      photos: allPhotos,
      photo_count: allPhotos.length,
      paper_type: paperType,
      discount_pct: applyDiscount ? discountPct : 0,
      original_price_lkr: applyDiscount ? originalPrice : undefined,
      is_bundle: isBundle,
      bundle_book_count: isBundle ? bundleBookCount : undefined,
      is_academic: isAcademic,
      institution: isAcademic ? institution : undefined,
      degree_stream: isAcademic ? degreeStream : undefined,
      academic_year: isAcademic ? academicYear : undefined,
      is_weekend_drop: isWeekendDrop,
      weekend_drop_price: isWeekendDrop ? weekendDropPrice : undefined,
      flip_test_video_url: flipTestVideoUrl || undefined,
      is_mystery_box: isMysteryBox,
      box_weight_kg: isMysteryBox ? boxWeightKg : undefined,
      box_genre_description: isMysteryBox ? boxGenreDescription : undefined,
      pin_potha_eligible: pinPothaEligible,
    };

    try {
      await createFullListing(payload);
      showToast('Listing created! ✅');
      setTimeout(() => navigate('/catalogue'), 500);
    } catch (err: any) {
      if (err.response?.status === 403) {
        setShowUpgradeModal(true);
      } else {
        showToast('Failed to create listing. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  // ── Section Header Component ───────────────────────────────────────────────
  const SectionHeader: React.FC<{
    id: string;
    emoji: string;
    title: string;
    subtitle?: string;
    badge?: string;
  }> = ({ id, emoji, title: sectionTitle, subtitle, badge }) => (
    <button
      type="button"
      onClick={() => toggleSection(id)}
      className="w-full flex items-center justify-between py-3 group"
    >
      <div className="flex items-center gap-3">
        <span className="text-xl">{emoji}</span>
        <div className="text-left">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-bold text-[#085041]">{sectionTitle}</h3>
            {badge && (
              <span className="px-2 py-0.5 bg-[#E1F5EE] text-[#085041] text-[10px] font-bold rounded-full uppercase tracking-wide">
                {badge}
              </span>
            )}
          </div>
          {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
        </div>
      </div>
      <div className="text-gray-400 group-hover:text-[#085041] transition-colors">
        {expandedSections[id] ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </div>
    </button>
  );

  // ── Toggle Switch Component ────────────────────────────────────────────────
  const ToggleSwitch: React.FC<{
    checked: boolean;
    onChange: (v: boolean) => void;
    label: string;
    id: string;
  }> = ({ checked, onChange, label, id }) => (
    <label htmlFor={id} className="flex items-center gap-3 cursor-pointer select-none">
      <div className="relative">
        <input
          type="checkbox"
          id={id}
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="sr-only peer"
        />
        <div className="w-10 h-5 bg-gray-300 rounded-full peer-checked:bg-[#1D9E75] transition-colors" />
        <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow-sm peer-checked:translate-x-5 transition-transform" />
      </div>
      <span className="text-sm font-semibold text-gray-700">{label}</span>
    </label>
  );

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#F1EFE8] animate-fade-in">
      {/* Sticky Top Bar */}
      <div className="sticky top-0 z-30 bg-[#F1EFE8]/95 backdrop-blur-sm border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-6 py-3 flex items-center justify-between">
          <button
            onClick={() => navigate('/catalogue')}
            className="flex items-center gap-2 text-sm font-semibold text-gray-600 hover:text-[#085041] transition-colors"
          >
            <ArrowLeft size={18} />
            Back to Catalogue
          </button>
          <h1 className="text-lg font-bold text-[#085041]">Create New Listing</h1>
          <div className="w-[120px]" /> {/* Spacer for centering */}
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-6 pb-32">
        {/* Validation Errors */}
        {errors.length > 0 && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4 animate-slide-up">
            <div className="flex items-start gap-3">
              <AlertCircle size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-bold text-red-700 mb-2">Please fix the following errors:</p>
                <ul className="space-y-1">
                  {errors.map((err, i) => (
                    <li key={i} className="text-xs text-red-600 flex items-start gap-1.5">
                      <span className="mt-0.5">•</span> {err}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════════
            SECTION 1: Basic Info
        ═══════════════════════════════════════════════════════════════════ */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm mb-4 overflow-hidden">
          <div className="px-5">
            <SectionHeader id="basic" emoji="📖" title="Basic Information" subtitle="Title, author, category, and pricing" badge="Required" />
          </div>
          {expandedSections.basic && (
            <div className="px-5 pb-5 border-t border-gray-100 pt-4 animate-fade-in">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Title — hidden when mystery box */}
                {!isMysteryBox && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Book Title *</label>
                    <input
                      id="field-title"
                      type="text"
                      value={title}
                      onChange={e => setTitle(e.target.value)}
                      placeholder="e.g. The Great Gatsby"
                      className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 focus:ring-2 focus:ring-[#085041]/30 focus:border-[#085041] outline-none text-sm transition-all"
                    />
                  </div>
                )}

                {/* Author — hidden when mystery box */}
                {!isMysteryBox && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Author *</label>
                    <input
                      id="field-author"
                      type="text"
                      value={author}
                      onChange={e => setAuthor(e.target.value)}
                      placeholder="e.g. F. Scott Fitzgerald"
                      className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 focus:ring-2 focus:ring-[#085041]/30 focus:border-[#085041] outline-none text-sm transition-all"
                    />
                  </div>
                )}

                {/* Category — hidden when mystery box */}
                {!isMysteryBox && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Category *</label>
                    <select
                      id="field-category"
                      value={category}
                      onChange={e => setCategory(e.target.value)}
                      className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 focus:ring-2 focus:ring-[#085041]/30 focus:border-[#085041] outline-none text-sm bg-white transition-all"
                    >
                      {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                  </div>
                )}

                {/* Condition */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Condition *</label>
                  <select
                    id="field-condition"
                    value={condition}
                    onChange={e => setCondition(e.target.value)}
                    className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 focus:ring-2 focus:ring-[#085041]/30 focus:border-[#085041] outline-none text-sm bg-white transition-all"
                  >
                    {CONDITIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>

                {/* Price */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Price LKR *</label>
                  <div className="relative">
                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sm font-semibold text-gray-400">Rs.</span>
                    <input
                      id="field-price"
                      type="number"
                      min="0"
                      value={priceLkr || ''}
                      onChange={e => setPriceLkr(parseInt(e.target.value) || 0)}
                      placeholder="0"
                      className="w-full pl-10 pr-3.5 py-2.5 rounded-lg border border-gray-200 focus:ring-2 focus:ring-[#085041]/30 focus:border-[#085041] outline-none text-sm transition-all"
                    />
                  </div>
                </div>

                {/* Description */}
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Description</label>
                  <textarea
                    id="field-description"
                    rows={3}
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    placeholder="Describe the book's condition, any highlights, notes, etc."
                    className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 focus:ring-2 focus:ring-[#085041]/30 focus:border-[#085041] outline-none text-sm resize-y transition-all"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ═══════════════════════════════════════════════════════════════════
            SECTION 2: Photos
        ═══════════════════════════════════════════════════════════════════ */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm mb-4 overflow-hidden">
          <div className="px-5">
            <SectionHeader id="photos" emoji="📸" title="Photos" subtitle="Minimum 3 required — Front, Back, Condition" badge="Required" />
          </div>
          {expandedSections.photos && (
            <div className="px-5 pb-5 border-t border-gray-100 pt-4 animate-fade-in">
              {/* Mandatory slots */}
              <div className="grid grid-cols-3 gap-3 mb-4">
                {photoSlots.map((slot, i) => (
                  <div key={i} className="relative group">
                    <label className={`cursor-pointer flex flex-col items-center justify-center rounded-xl border-2 border-dashed transition-all overflow-hidden aspect-[3/4]
                      ${slot.url
                        ? 'border-[#1D9E75] bg-[#E1F5EE]'
                        : 'border-gray-300 bg-gray-50 hover:border-[#1D9E75] hover:bg-[#E1F5EE]/50'
                      }`}
                    >
                      {slot.url ? (
                        <img src={slot.url} alt={slot.label} className="w-full h-full object-cover" />
                      ) : slot.uploading ? (
                        <div className="flex flex-col items-center gap-2 px-2">
                          <Loader2 size={24} className="animate-spin text-[#085041]" />
                          <span className="text-xs font-semibold text-[#085041]">{slot.progress}%</span>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-1.5 px-2 text-center">
                          <span className="text-2xl">{slot.emoji}</span>
                          <span className="text-[11px] font-semibold text-gray-500">{slot.label} *</span>
                        </div>
                      )}
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        disabled={slot.uploading}
                        onChange={e => {
                          const file = e.target.files?.[0];
                          if (file) handlePhotoSlotUpload(i, file);
                          e.target.value = '';
                        }}
                      />
                    </label>
                    {slot.url && (
                      <button
                        type="button"
                        onClick={() => removePhotoSlot(i)}
                        className="absolute -top-1.5 -right-1.5 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center shadow-lg opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                      >
                        <X size={12} />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {/* Extra photos */}
              <div className="flex flex-wrap gap-2 items-center">
                {extraPhotos.map((url, i) => (
                  <div key={i} className="relative group w-16 h-20 rounded-lg overflow-hidden border border-gray-200 shadow-sm">
                    <img src={url} alt={`Extra ${i + 1}`} className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removeExtraPhoto(i)}
                      className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center shadow opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X size={10} />
                    </button>
                  </div>
                ))}
                {allPhotos.length < 8 && (
                  <label className="cursor-pointer flex flex-col items-center justify-center w-16 h-20 rounded-lg border-2 border-dashed border-gray-300 hover:border-[#1D9E75] bg-gray-50 hover:bg-[#E1F5EE]/30 transition-all">
                    {extraPhotoUploading ? (
                      <Loader2 size={16} className="animate-spin text-gray-400" />
                    ) : (
                      <>
                        <span className="text-lg text-gray-400">+</span>
                        <span className="text-[9px] font-semibold text-gray-400">Add More</span>
                      </>
                    )}
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      disabled={extraPhotoUploading}
                      onChange={e => {
                        const file = e.target.files?.[0];
                        if (file) handleExtraPhotoUpload(file);
                        e.target.value = '';
                      }}
                    />
                  </label>
                )}
              </div>
              <p className="text-[11px] text-gray-400 mt-2">{allPhotos.length} / 8 photos uploaded</p>
            </div>
          )}
        </div>

        {/* ═══════════════════════════════════════════════════════════════════
            SECTION 3: Pricing & Discount
        ═══════════════════════════════════════════════════════════════════ */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm mb-4 overflow-hidden">
          <div className="px-5">
            <SectionHeader id="pricing" emoji="🏷️" title="Pricing & Discount" subtitle="Set discounts for better visibility" />
          </div>
          {expandedSections.pricing && (
            <div className="px-5 pb-5 border-t border-gray-100 pt-4 animate-fade-in">
              <ToggleSwitch
                id="toggle-discount"
                checked={applyDiscount}
                onChange={setApplyDiscount}
                label="Apply Discount?"
              />

              {applyDiscount && (
                <div className="mt-4 space-y-4 animate-fade-in">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">Original Price LKR</label>
                      <input
                        id="field-original-price"
                        type="number"
                        min="0"
                        value={originalPrice || ''}
                        onChange={e => setOriginalPrice(parseInt(e.target.value) || 0)}
                        className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 focus:ring-2 focus:ring-[#085041]/30 focus:border-[#085041] outline-none text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">Discount %</label>
                      <select
                        id="field-discount-pct"
                        value={discountPct}
                        onChange={e => setDiscountPct(parseInt(e.target.value))}
                        className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 focus:ring-2 focus:ring-[#085041]/30 focus:border-[#085041] outline-none text-sm bg-white"
                      >
                        {DISCOUNT_OPTIONS.map(d => <option key={d} value={d}>{d}%</option>)}
                      </select>
                    </div>
                  </div>

                  {/* Live Preview */}
                  {originalPrice > 0 && (
                    <div className="bg-gradient-to-r from-[#E1F5EE] to-[#e6f0ed] rounded-lg px-4 py-3 flex items-center gap-3 border border-[#1D9E75]/20">
                      <span className="text-lg font-bold text-[#085041]">LKR {discountedPrice.toLocaleString()}</span>
                      <span className="text-sm text-gray-400 line-through">LKR {originalPrice.toLocaleString()}</span>
                      <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-bold rounded-full">
                        🏷️ {discountPct}% OFF
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ═══════════════════════════════════════════════════════════════════
            SECTION 4: Paper Quality
        ═══════════════════════════════════════════════════════════════════ */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm mb-4 overflow-hidden">
          <div className="px-5">
            <SectionHeader id="paper" emoji="📄" title="Paper Quality" subtitle="Sri Lankan readers always ask this! Help them decide fast." />
          </div>
          {expandedSections.paper && (
            <div className="px-5 pb-5 border-t border-gray-100 pt-4 animate-fade-in">
              <div className="space-y-2.5">
                {[
                  { value: 'premium_white', label: 'Sudu Piti', desc: 'White pages — premium print', emoji: '⬜' },
                  { value: 'newsprint', label: 'Kalu Piti', desc: 'Newsprint — standard local', emoji: '⬛' },
                  { value: 'standard', label: 'Standard', desc: 'Regular book paper', emoji: '📖' },
                  { value: null as string | null, label: 'Not Sure / NA', desc: "Skip if you're unsure", emoji: '❓' },
                ].map((opt) => (
                  <label
                    key={opt.label}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg border-2 cursor-pointer transition-all
                      ${paperType === opt.value
                        ? 'border-[#085041] bg-[#E1F5EE]'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                  >
                    <input
                      type="radio"
                      name="paper_type"
                      checked={paperType === opt.value}
                      onChange={() => setPaperType(opt.value)}
                      className="sr-only"
                    />
                    <span className="text-xl">{opt.emoji}</span>
                    <div>
                      <p className="text-sm font-semibold text-gray-700">{opt.label}</p>
                      <p className="text-xs text-gray-500">{opt.desc}</p>
                    </div>
                    {paperType === opt.value && (
                      <CheckCircle2 size={18} className="ml-auto text-[#085041]" />
                    )}
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ═══════════════════════════════════════════════════════════════════
            SECTION 5: Book Bundle
        ═══════════════════════════════════════════════════════════════════ */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm mb-4 overflow-hidden">
          <div className="px-5">
            <SectionHeader id="bundle" emoji="📚" title="Book Bundle" subtitle="Sell multiple books as a single listing" />
          </div>
          {expandedSections.bundle && (
            <div className="px-5 pb-5 border-t border-gray-100 pt-4 animate-fade-in">
              <ToggleSwitch
                id="toggle-bundle"
                checked={isBundle}
                onChange={setIsBundle}
                label="This is a Bundle of Multiple Books?"
              />

              {isBundle && (
                <div className="mt-4 animate-fade-in">
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Number of books in bundle</label>
                  <input
                    id="field-bundle-count"
                    type="number"
                    min="2"
                    max="10"
                    value={bundleBookCount}
                    onChange={e => setBundleBookCount(Math.min(10, Math.max(2, parseInt(e.target.value) || 2)))}
                    className="w-32 px-3.5 py-2.5 rounded-lg border border-gray-200 focus:ring-2 focus:ring-[#085041]/30 focus:border-[#085041] outline-none text-sm"
                  />
                  <div className="mt-3 flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                    <Info size={14} className="text-blue-500 flex-shrink-0" />
                    <p className="text-xs text-blue-700">Buyers can purchase this as a complete bundle</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ═══════════════════════════════════════════════════════════════════
            SECTION 6: Kuppi Kade
        ═══════════════════════════════════════════════════════════════════ */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm mb-4 overflow-hidden">
          <div className="px-5">
            <SectionHeader id="kuppiKade" emoji="🎓" title="Kuppi Kade — Academic Market" subtitle="List for university & school students" />
          </div>
          {expandedSections.kuppiKade && (
            <div className="px-5 pb-5 border-t border-gray-100 pt-4 animate-fade-in">
              <ToggleSwitch
                id="toggle-academic"
                checked={isAcademic}
                onChange={setIsAcademic}
                label="📚 List in Kuppi Kade (Academic Market)?"
              />

              {isAcademic && (
                <div className="mt-4 space-y-4 animate-fade-in">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Institution *</label>
                    <input
                      id="field-institution"
                      type="text"
                      value={institution}
                      onChange={e => setInstitution(e.target.value)}
                      placeholder="e.g. University of Colombo"
                      className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 focus:ring-2 focus:ring-[#085041]/30 focus:border-[#085041] outline-none text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Degree / Stream</label>
                    <input
                      id="field-degree-stream"
                      type="text"
                      value={degreeStream}
                      onChange={e => setDegreeStream(e.target.value)}
                      placeholder="e.g. MBBS / Engineering"
                      className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 focus:ring-2 focus:ring-[#085041]/30 focus:border-[#085041] outline-none text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Academic Year</label>
                    <select
                      id="field-academic-year"
                      value={academicYear}
                      onChange={e => setAcademicYear(e.target.value)}
                      className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 focus:ring-2 focus:ring-[#085041]/30 focus:border-[#085041] outline-none text-sm bg-white"
                    >
                      {ACADEMIC_YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ═══════════════════════════════════════════════════════════════════
            SECTION 7: Weekend Maradana
        ═══════════════════════════════════════════════════════════════════ */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm mb-4 overflow-hidden">
          <div className="px-5">
            <SectionHeader id="maradana" emoji="🛒" title="Weekend Maradana Flash Market" subtitle="Special visibility during weekends" />
          </div>
          {expandedSections.maradana && (
            <div className="px-5 pb-5 border-t border-gray-100 pt-4 animate-fade-in">
              {/* Info box — always visible */}
              <div className="flex items-start gap-2.5 bg-amber-50 border border-amber-200 rounded-lg px-3.5 py-2.5 mb-4">
                <Info size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-800 leading-relaxed">
                  <span className="font-bold">Weekend Maradana</span> runs Friday 6:00 PM → Sunday 11:59 PM.
                  Your listing gets special visibility during this window.
                </p>
              </div>

              <ToggleSwitch
                id="toggle-weekend-drop"
                checked={isWeekendDrop}
                onChange={setIsWeekendDrop}
                label="🛒 List for Weekend Maradana Flash Market?"
              />

              {isWeekendDrop && (
                <div className="mt-4 animate-fade-in">
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Weekend Drop Price LKR</label>
                  <div className="relative w-48">
                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sm font-semibold text-gray-400">Rs.</span>
                    <input
                      id="field-weekend-price"
                      type="number"
                      min="0"
                      value={weekendDropPrice || ''}
                      onChange={e => setWeekendDropPrice(parseInt(e.target.value) || 0)}
                      className="w-full pl-10 pr-3.5 py-2.5 rounded-lg border border-gray-200 focus:ring-2 focus:ring-[#085041]/30 focus:border-[#085041] outline-none text-sm"
                    />
                  </div>
                  {weekendDropPrice > 0 && priceLkr > 0 && weekendDropPrice < priceLkr && (
                    <p className="text-xs text-green-600 mt-2 font-medium">
                      💰 {Math.round(((priceLkr - weekendDropPrice) / priceLkr) * 100)}% lower than regular price!
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ═══════════════════════════════════════════════════════════════════
            SECTION 8: Flip-Test Video (PRICE-GATED)
        ═══════════════════════════════════════════════════════════════════ */}
        {needsFlipTest && (
          <div className="bg-white rounded-xl border border-sky-200 shadow-sm mb-4 overflow-hidden animate-fade-in">
            <div className="px-5">
              <SectionHeader id="flipTest" emoji="🎬" title="Flip-Test Video" subtitle="Required for listings LKR 2,000+" badge="Required" />
            </div>
            {expandedSections.flipTest && (
              <div className="px-5 pb-5 border-t border-sky-100 pt-4 animate-fade-in">
                <p className="text-xs text-gray-500 mb-4">
                  Show buyers the book condition in 15 seconds. <span className="font-semibold">Max 10MB / MP4 only.</span>
                </p>

                {flipTestVideoUrl ? (
                  <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-lg px-4 py-3">
                    <CheckCircle2 size={20} className="text-green-600" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-green-700">✅ Video Uploaded</p>
                      <p className="text-xs text-green-600 truncate">{videoFileName}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => { setFlipTestVideoUrl(null); setVideoFileName(''); }}
                      className="text-xs text-red-500 font-semibold hover:text-red-700"
                    >
                      Remove
                    </button>
                  </div>
                ) : videoUploading ? (
                  <div className="border border-gray-200 rounded-lg px-4 py-4">
                    <div className="flex items-center gap-3 mb-2">
                      <Loader2 size={20} className="animate-spin text-[#085041]" />
                      <span className="text-sm font-semibold text-gray-700">Uploading {videoFileName}...</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                      <div
                        className="bg-gradient-to-r from-[#085041] to-[#1D9E75] h-2.5 rounded-full transition-all duration-300"
                        style={{ width: `${videoProgress}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1 text-right">{videoProgress}%</p>
                  </div>
                ) : (
                  <label className="cursor-pointer flex flex-col items-center justify-center py-8 rounded-xl border-2 border-dashed border-gray-300 hover:border-[#085041] bg-gray-50 hover:bg-[#E1F5EE]/30 transition-all group">
                    <div className="w-14 h-14 rounded-full bg-[#E1F5EE] flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                      <Film size={24} className="text-[#085041]" />
                    </div>
                    <span className="text-sm font-semibold text-gray-600 group-hover:text-[#085041]">Click to upload video</span>
                    <span className="text-xs text-gray-400 mt-1">MP4 · Max 10MB · ~15 seconds</span>
                    <input
                      type="file"
                      className="hidden"
                      accept="video/mp4"
                      onChange={e => {
                        const file = e.target.files?.[0];
                        if (file) handleVideoUpload(file);
                        e.target.value = '';
                      }}
                    />
                  </label>
                )}
              </div>
            )}
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════════
            SECTION 9: Kilo Pettiya
        ═══════════════════════════════════════════════════════════════════ */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm mb-4 overflow-hidden">
          <div className="px-5">
            <SectionHeader id="kiloPettiya" emoji="📦" title="Kilo Pettiya — Mystery Book Box" subtitle="Sell a mystery collection by weight" />
          </div>
          {expandedSections.kiloPettiya && (
            <div className="px-5 pb-5 border-t border-gray-100 pt-4 animate-fade-in">
              <div className="flex items-start gap-2.5 bg-amber-50 border border-amber-200 rounded-lg px-3.5 py-2.5 mb-4">
                <Info size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-800 leading-relaxed">
                  Sell a mystery collection by weight. Buyers don't know exact titles — gacha experience!
                  <span className="font-bold"> Max 1 active mystery box per account.</span>
                </p>
              </div>

              <ToggleSwitch
                id="toggle-mystery-box"
                checked={isMysteryBox}
                onChange={setIsMysteryBox}
                label="📦 List as Kilo Pettiya (Mystery Book Box)?"
              />

              {isMysteryBox && (
                <div className="mt-4 space-y-4 animate-fade-in">
                  {/* Warning */}
                  <div className="flex items-start gap-2.5 bg-red-50 border border-red-200 rounded-lg px-3.5 py-2.5">
                    <AlertCircle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-red-700 leading-relaxed">
                      You can only have 1 active Kilo Pettiya listing. Creating this will remove your current mystery box if one exists.
                    </p>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Box Weight (kg) *</label>
                    <input
                      id="field-box-weight"
                      type="number"
                      min="0.5"
                      max="20"
                      step="0.5"
                      value={boxWeightKg}
                      onChange={e => setBoxWeightKg(parseFloat(e.target.value) || 1)}
                      className="w-32 px-3.5 py-2.5 rounded-lg border border-gray-200 focus:ring-2 focus:ring-[#085041]/30 focus:border-[#085041] outline-none text-sm"
                    />
                    <span className="text-xs text-gray-400 ml-2">kg</span>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                      Genre Description * <span className="text-gray-400 font-normal">({boxGenreDescription.length}/200)</span>
                    </label>
                    <textarea
                      id="field-box-genre"
                      rows={2}
                      maxLength={200}
                      value={boxGenreDescription}
                      onChange={e => setBoxGenreDescription(e.target.value)}
                      placeholder="e.g. Sinhala novels and short story collections"
                      className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 focus:ring-2 focus:ring-[#085041]/30 focus:border-[#085041] outline-none text-sm resize-none"
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ═══════════════════════════════════════════════════════════════════
            SECTION 10: Pin Potha
        ═══════════════════════════════════════════════════════════════════ */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm mb-4 overflow-hidden">
          <div className="px-5">
            <SectionHeader id="pinPotha" emoji="📌" title="Pin Potha — Donate Option" subtitle="Support rural school students" />
          </div>
          {expandedSections.pinPotha && (
            <div className="px-5 pb-5 border-t border-gray-100 pt-4 animate-fade-in">
              <div className="flex items-start gap-2.5 bg-purple-50 border border-purple-200 rounded-lg px-3.5 py-2.5 mb-4">
                <Info size={16} className="text-purple-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-purple-700 leading-relaxed">
                  Rural school students can receive this book as a donation.
                  You earn <span className="font-bold">Sathkarya Aura</span> (cosmetic badge) — not XP.
                </p>
              </div>

              <ToggleSwitch
                id="toggle-pin-potha"
                checked={pinPothaEligible}
                onChange={setPinPothaEligible}
                label="📌 Allow this as a Pin Potha donation?"
              />
            </div>
          )}
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════
          STICKY SUBMIT BAR
      ═══════════════════════════════════════════════════════════════════ */}
      <div className="fixed bottom-0 left-0 right-0 z-30 bg-white/95 backdrop-blur-sm border-t border-gray-200 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]">
        <div className="max-w-3xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="hidden sm:flex items-center gap-3 text-xs text-gray-500">
            <span className="flex items-center gap-1">
              📸 {allPhotos.length}/8 photos
            </span>
            {needsFlipTest && (
              <span className={`flex items-center gap-1 ${flipTestVideoUrl ? 'text-green-600' : 'text-red-500'}`}>
                🎬 {flipTestVideoUrl ? '✅' : '❌'} Flip-Test
              </span>
            )}
          </div>
          <button
            id="btn-create-listing"
            type="button"
            onClick={handleSubmit}
            disabled={saving}
            className="px-8 py-3 bg-[#085041] text-white rounded-xl font-bold text-sm hover:bg-teal-900 transition-all shadow-lg shadow-[#085041]/20 disabled:opacity-50 flex items-center gap-2 ml-auto"
          >
            {saving && <Loader2 size={16} className="animate-spin" />}
            + Create Listing
          </button>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════
          UPGRADE MODAL (403)
      ═══════════════════════════════════════════════════════════════════ */}
      {showUpgradeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowUpgradeModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-sm w-full p-8 text-center animate-slide-up">
            <div className="w-16 h-16 rounded-2xl bg-amber-100 flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">🔒</span>
            </div>
            <h3 className="text-lg font-bold text-[#085041] mb-2">Listing Limit Reached</h3>
            <p className="text-sm text-gray-600 mb-6">
              You've reached your 5 listing limit. Upgrade to Pro for unlimited listings.
            </p>
            <button
              onClick={() => navigate('/settings')}
              className="w-full py-3 bg-gradient-to-r from-[#085041] to-[#1D9E75] text-white rounded-xl font-bold text-sm hover:opacity-90 transition-opacity shadow-lg"
            >
              Upgrade →
            </button>
            <button
              onClick={() => setShowUpgradeModal(false)}
              className="w-full py-2 text-sm text-gray-500 font-medium mt-2 hover:text-gray-700"
            >
              Maybe Later
            </button>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════
          TOAST
      ═══════════════════════════════════════════════════════════════════ */}
      {toastMessage && (
        <div className="fixed bottom-20 right-6 z-50 bg-[#085041] text-white px-5 py-3 rounded-xl shadow-xl text-sm font-bold animate-slide-up">
          {toastMessage}
        </div>
      )}
    </div>
  );
};

export default CreateListing;
